// Binary responses (PDFs, PNGs, containers, a version's bytes) behind
// docs-api, fetched as blobs and handed to the browser as object URLs. The
// session cookie authenticates (@zehnmindai/embed's bffFetch: docs' own
// origin only); a guest page passes its guest token in `headers`, which wins
// at docs-api. Never camelized — bytes, not JSON.
import { useEffect, useState } from 'react';
import { bffFetch } from '@zehnmindai/embed/bff';
import { apiPath } from './request';

export async function fetchAuthedBlob(path, { headers = {} } = {}) {
  const res = await bffFetch(apiPath(path), { headers });
  if (!res.ok) {
    let detail = null;
    try {
      detail = await res.json();
    } catch {
      /* not JSON */
    }
    const err = new Error(detail?.detail?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.detail = detail?.detail || null;
    throw err;
  }
  return res.blob();
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** "Report (v3).docx" for version 3 of "Report.docx". */
export function versionFileName(name, versionNumber) {
  const fileName = name || 'document';
  const dot = fileName.lastIndexOf('.');
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const ext = dot > 0 ? fileName.slice(dot) : '';
  return `${stem} (v${versionNumber})${ext}`;
}

export function openBlob(blob) {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// An object URL for an authenticated image/PDF, revoked on unmount or when
// the path changes. `null` while loading or when the fetch failed.
export function useAuthedObjectUrl(path, { headers, enabled = true } = {}) {
  const [url, setUrl] = useState(null);
  const headerKey = JSON.stringify(headers || {});
  useEffect(() => {
    if (!path || !enabled) {
      setUrl(null);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    fetchAuthedBlob(path, { headers: headers || {} })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled, headerKey]);
  return url;
}
